import { httpClient } from "@/lib/http";
import { MESSAGING_ENDPOINTS } from "@/config/api";
import type { ApiResponse } from "@/types/api";
import type { ApprovalSubmission } from "@/features/approvals/types";
import type {
    SendPushNotificationToUserRequest,
    CreateAnnouncementRequest,
    ScheduleAnnouncementRequest,
    AnnouncementListItem,
    AnnouncementDetails,
    AnnouncementMutationResult,
    AnnouncementSendResponse,
    AnnouncementCancelResponse,
    AnnouncementsListResponse,
    AnnouncementsQueryParams,
    MessagingStatsResponse,
    MessagingStatsQueryParams,
} from "../types";

const ANNOUNCEMENT_REQUEST_TIMEOUT = 60_000;

type AnnouncementWritePayload = CreateAnnouncementRequest | ScheduleAnnouncementRequest | FormData;

const getAnnouncementRequestConfig = (payload: AnnouncementWritePayload) =>
    payload instanceof FormData
        ? {
              timeout: ANNOUNCEMENT_REQUEST_TIMEOUT,
          }
        : {
              timeout: ANNOUNCEMENT_REQUEST_TIMEOUT,
          };

// ============================================================================
// Push Notifications
// ============================================================================

/** 13.4 – Send push notification to a specific user */
export const sendPushNotificationToUser = async (
    payload: SendPushNotificationToUserRequest
): Promise<ApiResponse<ApprovalSubmission>> => {
    const { data } = await httpClient.post<ApiResponse<ApprovalSubmission>>(
        MESSAGING_ENDPOINTS.PUSH_USER,
        payload
    );
    return data;
};

// ============================================================================
// Announcements
// ============================================================================

/** 13.5 – Create & send announcement */
export const createAndSendAnnouncement = async (
    payload: CreateAnnouncementRequest | FormData
): Promise<ApiResponse<ApprovalSubmission>> => {
    const { data } = await httpClient.post<ApiResponse<ApprovalSubmission>>(
        MESSAGING_ENDPOINTS.ANNOUNCEMENTS,
        payload,
        getAnnouncementRequestConfig(payload)
    );
    return data;
};

/** 13.6 – Create draft announcement */
export const createDraftAnnouncement = async (
    payload: CreateAnnouncementRequest | FormData
): Promise<ApiResponse<AnnouncementMutationResult>> => {
    const { data } = await httpClient.post<ApiResponse<AnnouncementMutationResult>>(
        MESSAGING_ENDPOINTS.ANNOUNCEMENTS_DRAFT,
        payload,
        getAnnouncementRequestConfig(payload)
    );
    return data;
};

/** 13.7 – Schedule announcement */
export const scheduleAnnouncement = async (
    payload: ScheduleAnnouncementRequest | FormData
): Promise<ApiResponse<ApprovalSubmission>> => {
    const { data } = await httpClient.post<ApiResponse<ApprovalSubmission>>(
        MESSAGING_ENDPOINTS.ANNOUNCEMENTS_SCHEDULE,
        payload,
        getAnnouncementRequestConfig(payload)
    );
    return data;
};

/** 13.8 – Send existing announcement */
export const sendAnnouncement = async (
    id: string
): Promise<ApiResponse<ApprovalSubmission>> => {
    const { data } = await httpClient.post<ApiResponse<ApprovalSubmission>>(
        MESSAGING_ENDPOINTS.ANNOUNCEMENT_SEND(id)
    );
    return data;
};

/** 13.9 – Cancel scheduled announcement */
export const cancelAnnouncement = async (
    id: string
): Promise<ApiResponse<AnnouncementCancelResponse>> => {
    const { data } = await httpClient.post<ApiResponse<AnnouncementCancelResponse>>(
        MESSAGING_ENDPOINTS.ANNOUNCEMENT_CANCEL(id)
    );
    return data;
};

/** 13.10 – List announcements */
export const listAnnouncements = async (
    params?: AnnouncementsQueryParams
): Promise<ApiResponse<AnnouncementsListResponse>> => {
    const { data } = await httpClient.get<ApiResponse<AnnouncementsListResponse>>(
        MESSAGING_ENDPOINTS.ANNOUNCEMENTS,
        { params }
    );
    return data;
};

/** 13.11 – Get announcement details */
export const getAnnouncementDetails = async (
    id: string
): Promise<ApiResponse<AnnouncementDetails>> => {
    const { data } = await httpClient.get<ApiResponse<AnnouncementDetails>>(
        MESSAGING_ENDPOINTS.ANNOUNCEMENT_DETAILS(id)
    );
    return data;
};

// ============================================================================
// Messaging Stats
// ============================================================================

export const getMessagingStats = async (
    params?: MessagingStatsQueryParams
): Promise<ApiResponse<MessagingStatsResponse>> => {
    const { data } = await httpClient.get<ApiResponse<MessagingStatsResponse>>(
        MESSAGING_ENDPOINTS.STATS,
        { params }
    );
    return data;
};
